import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import {
  Briefcase,
  CircleX,
  ClipboardCheck,
  Search,
  Star,
  UserCheck,
  Users,
  UserSearch,
} from 'lucide-react';
import styles from './NoApplicantsEmptyState.module.scss';

const NoApplicantsEmptyState = ({
  tab = 'invited',
  browseURL = '/browse',
  compact = false,
}) => {
  const getEmptyStateContent = () => {
    switch (tab) {
      case 'talentPool':
        return {
          icon: <Users width={68} height={68} className={styles.icon} />,
          title: 'Your talent pool is empty',
          description:
            'Browse our network and add experts to your talent pool.',
          ctaText: 'Browse experts',
          ctaHref: browseURL,
        };

      case 'jobsCarousel':
        return {
          icon: <Briefcase width={68} height={68} />,
          title: 'No active jobs posted',
          description: 'Post a job to get started.',
          ctaText: 'Post a job',
          ctaHref: '/post-a-job',
        };

      case 'applied':
        return {
          icon: <Users width={68} height={68} className={styles.icon} />,
          title: 'No experts have applied',
          description:
            'Browse our network and invite experts to apply to this job.',
          ctaText: 'Browse experts',
          ctaHref: browseURL,
        };

      case 'declined':
        return {
          icon: <CircleX width={68} height={68} />,
          title: 'No applicants declined',
          description: null,
          ctaText: null,
          ctaHref: null,
        };

      case 'shortlisted':
        return {
          icon: <Star width={68} height={68} />,
          title: 'No applicants shortlisted',
          description: null,
          ctaText: null,
          ctaHref: null,
        };

      case 'reviewed':
        return {
          icon: <ClipboardCheck width={68} height={68} />,
          title: 'No applicants reviewed',
          description: null,
          ctaText: null,
          ctaHref: null,
        };

      case 'hired':
        return {
          icon: <UserCheck width={68} height={68} className={styles.icon} />,
          title: 'No applicants hired',
          description: null,
          ctaText: null,
          ctaHref: null,
        };

      case 'filtered':
        return {
          icon: <Search width={68} height={68} />,
          title: 'No applicants that match your search',
          description: 'Try adjusting your filters to see more applicants.',
          ctaText: null,
          ctaHref: null,
        };

      case 'noSelected':
        return {
          icon: <Users width={68} height={68} className={styles.icon} />,
          title: 'No applicant selected',
          description:
            'Choose an applicant from the list on the left to see their details here.',
          ctaText: null,
          ctaHref: null,
        };

      case 'invited':
      default:
        return {
          icon: <UserSearch width={68} height={68} className={styles.icon} />,
          title: 'No experts invited',
          description:
            'Browse our network and invite experts to apply to this job.',
          ctaText: 'Browse experts',
          ctaHref: browseURL,
        };
    }
  };

  const content = getEmptyStateContent();

  return (
    <div
      className={`${styles.emptyStateContainer} ${
        compact ? styles.compactEmptyStateContainer : ''
      }`}
    >
      <div
        className={`${styles.emptyStateContent} ${
          compact ? styles.compactEmptyStateContent : ''
        }`}
      >
        <div
          className={`${styles.imageContainer} ${
            compact ? styles.compactImageContainer : ''
          }`}
        >
          <div className={styles.image}>{content.icon}</div>
        </div>

        <h3 className={styles.title}>{content.title}</h3>

        {content.description && (
          <p className={styles.description}>{content.description}</p>
        )}

        {content.ctaText && content.ctaHref && (
          <Button
            bsStyle="primary"
            bsSize="large"
            href={content.ctaHref}
            className={styles.ctaButton}
          >
            {content.ctaText}
          </Button>
        )}
      </div>
    </div>
  );
};

NoApplicantsEmptyState.propTypes = {
  tab: PropTypes.oneOf([
    'talentPool',
    'applied',
    'declined',
    'shortlisted',
    'hired',
    'invited',
    'filtered',
    'noSelected',
  ]),
  browseURL: PropTypes.string,
  compact: PropTypes.bool,
};

NoApplicantsEmptyState.defaultProps = {
  tab: 'invited',
  browseURL: '/browse',
  compact: false,
};

export default NoApplicantsEmptyState;
